import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import type {
  BetaFeatureEnrollmentResponse,
  BetaFeaturesListResponse,
  FamilyMembersResponse,
} from "@family/shared";

interface Props {
  familySlug: string;
}

/**
 * Admin UI for granting / revoking beta features per user. One feature
 * row per known key, expand to see enrolled users and add new ones.
 */
export function BetaFeaturesAdmin({ familySlug }: Props) {
  const featuresQuery = useQuery<BetaFeaturesListResponse>({
    queryKey: ["beta-features", familySlug],
    queryFn: () =>
      apiFetch<BetaFeaturesListResponse>(`/families/${familySlug}/beta/features`),
  });

  if (featuresQuery.isLoading) return <p className="text-muted">Loading…</p>;
  if (featuresQuery.error)
    return <p className="text-error">{(featuresQuery.error as Error).message}</p>;

  const features = featuresQuery.data?.features ?? [];
  if (features.length === 0) {
    return <p className="text-muted">No beta features available.</p>;
  }

  return (
    <div className="stack">
      {features.map((f) => (
        <FeatureRow key={f.key} familySlug={familySlug} featureKey={f.key} label={f.label} description={f.description} enrolledCount={f.enrolledCount} />
      ))}
    </div>
  );
}

function FeatureRow({
  familySlug,
  featureKey,
  label,
  description,
  enrolledCount,
}: {
  familySlug: string;
  featureKey: string;
  label: string;
  description: string;
  enrolledCount: number;
}) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const enrollmentQuery = useQuery<BetaFeatureEnrollmentResponse>({
    queryKey: ["beta-feature", familySlug, featureKey],
    queryFn: () =>
      apiFetch<BetaFeatureEnrollmentResponse>(
        `/families/${familySlug}/beta/features/${featureKey}`,
      ),
    enabled: open,
  });

  const membersQuery = useQuery<FamilyMembersResponse>({
    queryKey: ["members", familySlug],
    queryFn: () =>
      apiFetch<FamilyMembersResponse>(`/families/${familySlug}/members`),
    enabled: open,
  });

  const grant = useMutation({
    mutationFn: (userId: string) =>
      apiFetch(`/families/${familySlug}/beta/features/${featureKey}`, {
        method: "POST",
        body: JSON.stringify({ userId }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["beta-feature", familySlug, featureKey],
      });
      void queryClient.invalidateQueries({
        queryKey: ["beta-features", familySlug],
      });
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });

  const revoke = useMutation({
    mutationFn: (userId: string) =>
      apiFetch(
        `/families/${familySlug}/beta/features/${featureKey}/users/${userId}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["beta-feature", familySlug, featureKey],
      });
      void queryClient.invalidateQueries({
        queryKey: ["beta-features", familySlug],
      });
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });

  const enrolledIds = new Set(
    (enrollmentQuery.data?.users ?? []).map((u) => u.userId),
  );
  const allMembers = membersQuery.data?.members ?? [];
  const candidates = allMembers.filter((m) => !enrolledIds.has(m.userId));

  return (
    <div className="card stack">
      <div className="row-between">
        <div>
          <strong>{label}</strong>
          <div className="text-subtle" style={{ fontSize: "0.85rem", marginTop: 4 }}>
            {description}
          </div>
          <div className="text-subtle" style={{ fontSize: "0.8rem", marginTop: 4 }}>
            <code>{featureKey}</code> · {enrolledCount} enrolled
          </div>
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setOpen((o) => !o)}
          style={{ minHeight: 32, padding: "4px 12px", fontSize: "0.85rem" }}
        >
          {open ? "Hide" : "Manage"}
        </button>
      </div>

      {open && (
        <div className="stack" style={{ borderTop: "1px solid var(--c-border)", paddingTop: "var(--s-3)" }}>
          <h4 style={{ margin: 0 }}>Enrolled</h4>
          {enrollmentQuery.isLoading && <p className="text-muted">Loading…</p>}
          {enrollmentQuery.data?.users.length === 0 && (
            <p className="text-muted" style={{ fontSize: "0.85rem" }}>
              No one enrolled yet.
            </p>
          )}
          <ul className="list-unstyled">
            {enrollmentQuery.data?.users.map((u) => (
              <li
                key={u.userId}
                className="row-between"
                style={{ padding: "8px 0", borderBottom: "1px dashed var(--c-border)" }}
              >
                <div>
                  <strong>{u.displayName ?? u.email ?? u.phone ?? u.userId}</strong>
                  <div className="text-subtle" style={{ fontSize: "0.8rem" }}>
                    enrolled {new Date(u.grantedAt).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" })}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-tertiary"
                  onClick={() => revoke.mutate(u.userId)}
                  disabled={revoke.isPending}
                  style={{ padding: "4px 10px", fontSize: "0.8rem", color: "var(--c-danger)" }}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>

          <h4 style={{ margin: 0, marginTop: "var(--s-3)" }}>Add user</h4>
          {candidates.length === 0 && allMembers.length > 0 && (
            <p className="text-muted" style={{ fontSize: "0.85rem" }}>
              All members are already enrolled.
            </p>
          )}
          {candidates.length > 0 && (
            <AddUserPicker
              candidates={candidates}
              onPick={(userId) => grant.mutate(userId)}
              disabled={grant.isPending}
            />
          )}

          {(grant.error || revoke.error) && (
            <p className="text-error">
              {(grant.error ?? revoke.error)?.message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function AddUserPicker({
  candidates,
  onPick,
  disabled,
}: {
  candidates: FamilyMembersResponse["members"];
  onPick: (userId: string) => void;
  disabled: boolean;
}) {
  const [selected, setSelected] = useState("");
  return (
    <div style={{ display: "flex", gap: "var(--s-2)" }}>
      <select
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        disabled={disabled}
        className="select-skapa"
        style={{ flex: 1 }}
      >
        <option value="">Pick a member…</option>
        {candidates.map((m) => (
          <option key={m.userId} value={m.userId}>
            {m.displayName ?? m.email ?? m.phone ?? m.userId} ({m.role})
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => {
          if (selected) {
            onPick(selected);
            setSelected("");
          }
        }}
        disabled={disabled || !selected}
        style={{ minHeight: 34, padding: "0 14px", fontSize: "0.85rem" }}
      >
        Enrol
      </button>
    </div>
  );
}
